import type { FilterFn } from "@tanstack/vue-table";
import type { ComponentPropsOptions, ExtractPublicPropTypes, PropType } from "vue";
import type { FetchOptions } from "../../combobox/Combobox";
import type { GlobalOptionsSelect, OptionSelect, OptionValue } from "@/components/helpers/types";
import type { FlexComponent } from "@/utils/render-component";
import { debounce } from "lodash-es";
import { match } from "ts-pattern";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { toOptionSelect } from "@/components/helpers/types";
import Spinner from "@/components/Spinner";
import { $t } from "@/services/i18n";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Badge from "../../badge/Badge";
import { useRemote } from "../../combobox/Combobox";
import Command from "../../command/Command";
import FacetedFilter from "../../faceted-filter/FacetedFilter";
import Icon from "../../icon/Icon";
import { useTableEmitter } from "../composables/helpers";
import { injectTable } from "../DataTable";
import { defaultFacetedProps } from "./props";

type ListValue = OptionValue;

export type SingleListFilter = {
  _type: "list";
  operator: "eq";
  mode: "single";
  value: ListValue;
  filterFunction?: (rowValue: any, selectedValues: ListValue[]) => boolean;
};

export type MultipleListFilter = {
  _type: "list";
  operator: "inq";
  mode: "multiple";
  value: ListValue[];
  filterFunction?: (rowValue: any, selectedValues: ListValue[]) => boolean;
};

export type ListFilter = SingleListFilter | MultipleListFilter;

export function formatListFilter(
  value: ListValue | ListValue[],
  filterFunction?: (rowValue: any, selectedValues: ListValue[]) => boolean
): ListFilter {
  const mode = Array.isArray(value) ? "multiple" : "single";
  return {
    _type: "list",
    operator: mode === "multiple" ? "inq" : "eq",
    mode,
    value: value,
    filterFunction,
  } as ListFilter;
}

const listProps = {
  ...defaultFacetedProps,
  open: p.boolean(false),
  options: {
    type: [Array, Function] as PropType<GlobalOptionsSelect[] | FetchOptions>,
    required: true,
  },
  mode: p.string<"single" | "multiple">("single"),
  initialValue: p.custom<ListValue | ListValue[] | null>([String, Array]),
  filterFunction: {
    type: Function as PropType<(rowValue: any, selectedValues: ListValue[]) => boolean>,
    required: false,
  },
} satisfies ComponentPropsOptions;

export type ListFacetedFilterProps = ExtractPublicPropTypes<typeof listProps>;

export default defineComponent({
  name: "ListFacetedFilter",
  props: listProps,
  setup(props) {
    const table = injectTable();
    const column = table.getColumn(props.accessorKey);
    const open = ref(true);

    const { loading, searchOptions, searchTerm } = useRemote(props.options, open);

    const options = computed<OptionSelect[]>(() => {
      if (typeof props.options === "function") {
        return searchOptions.value.map(toOptionSelect);
      }

      return props.options?.map(toOptionSelect) ?? [];
    });

    const selected = ref<OptionValue[]>([]);

    // Helper function to get column filter value
    function getColumnFilterValue() {
      const columnFilter = column?.getFilterValue();
      if (!isListFilter(columnFilter)) return null;

      return columnFilter.mode === "single" ? [columnFilter.value] : columnFilter.value;
    }

    // Helper function to get initial value from props
    function getInitialValueFromProps() {
      if (!props.initialValue) return null;
      return Array.isArray(props.initialValue) ? props.initialValue : [props.initialValue];
    }

    // Main function to get initial value
    function getInitialValue(): OptionValue[] | null {
      return props.initialValue ? getInitialValueFromProps() : getColumnFilterValue();
    }

    onMounted(() => {
      const initialValue = getInitialValue();
      if (initialValue) selected.value = initialValue;
    });

    const columnFilter = computed(() => {
      const filter = column?.getFilterValue();

      if (!isListFilter(filter)) return undefined;
      return filter;
    });

    watch(columnFilter, (val) => {
      if (!val) {
        selected.value = [];
        return;
      }
    });

    watch(
      selected,
      debounce((val) => {
        if (!column) return;

        if (!val.length) {
          column.setFilterValue(null);
          return;
        }

        if (props.mode === "single") {
          const value = formatListFilter(val[0], props.filterFunction);

          column.setFilterValue({
            ...value,
          });
          return;
        }

        column.setFilterValue(formatListFilter(val, props.filterFunction));
      }, props.debounce)
    );

    function onSelectedChange(value: OptionValue | OptionSelect) {
      const val = value && typeof value === "object" ? value.value : value;

      if (!val) {
        return;
      }

      if (props.mode === "single") {
        selected.value = [val];
        return;
      }

      if (selected.value.includes(val)) {
        selected.value = selected.value.filter((i) => i !== val);
        return;
      }

      selected.value = [...selected.value, val];
    }

    function onClear() {
      selected.value = [];
    }

    function getLabel(val?: OptionValue) {
      if (!val) return "";
      return options.value.find((i) => i.value === val)?.label;
    }

    const Badges = computed<FlexComponent | undefined>(() => {
      if (!selected.value?.length) return undefined;
      if (selected.value?.length > 2)
        return (
          <Badge variant="muted" size="xs">
            {selected.value.length} {$t("table.selected_other")}
          </Badge>
        );

      return (
        <div class="space-x-1">
          {selected.value?.map((item) => {
            return (
              <Badge variant="muted" size="xs">
                <span class="max-w-40 overflow-hidden text-ellipsis">{getLabel(item)}</span>
              </Badge>
            );
          })}
        </div>
      );
    });

    const facetedFilterComp = ref<{
      close: () => void;
    }>();

    useTableEmitter("set-filter", (filter) => {
      if (filter.id !== table.id || filter.column !== props.accessorKey) return;

      const payload = filter.setter({
        value: selected.value,
      });

      if (payload) {
        selected.value = payload.value;
      }
    });

    return () => (
      <FacetedFilter
        ref={facetedFilterComp}
        label={props.label}
        badges={Badges.value}
        size={props.size}
        active={!!selected.value?.length}
      >
        <Command.Root class="min-w-0 max-w-sm">
          <div class="relative-w-full">
            <Command.Input autoFocus v-model={searchTerm.value} />
            {loading.value && <Spinner class="absolute right-3 top-3 h-4 w-4" />}
          </div>
          <Command.List
            class="max-w-60 overflow-hidden"
            onEscapeKeyDown={() => {
              facetedFilterComp.value?.close();
            }}
            v-slots={{
              default: () => (
                <>
                  <Command.Empty class="p-5">{$t("table.no_results_found")}</Command.Empty>
                  <Command.Group>
                    {options.value.map((item) => {
                      return (
                        <Command.Item
                          onSelect={onSelectedChange}
                          key={item.value?.toString() as string}
                          value={item as OptionSelect}
                        >
                          {match(props.mode)
                            .with("single", () => (
                              <>
                                <div class="center mr-2 flex h-4 w-4 shrink-0 whitespace-nowrap">
                                  <Icon
                                    icon="lucide:check"
                                    v-show={selected.value.includes(item.value)}
                                    class="shrink-0"
                                  />
                                </div>
                                <span
                                  class="max-w-48 overflow-hidden truncate"
                                  title={typeof item.label === "string" ? item.label : undefined}
                                >
                                  {item.label}
                                </span>
                              </>
                            ))
                            .with("multiple", () => (
                              <>
                                <div
                                  class={cn(
                                    "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border p-px",
                                    selected.value.includes(item.value)
                                      ? "border-brand-9 bg-brand-9 text-background"
                                      : "opacity-80 [&_svg]:invisible"
                                  )}
                                >
                                  <Icon icon="lucide:check" class="h-3.5 w-3.5" />
                                </div>
                                <span
                                  class="max-w-48 overflow-hidden truncate"
                                  title={typeof item.label === "string" ? item.label : undefined}
                                >
                                  {item.label}
                                </span>
                              </>
                            ))
                            .exhaustive()}
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                </>
              ),
              footer: () =>
                !!selected.value?.length && (
                  <Command.Footer>
                    <Command.Item value={" "} class="justify-center py-1 text-center" onSelect={onClear}>
                      {$t("clear_filter")}
                    </Command.Item>
                  </Command.Footer>
                ),
            }}
          />
        </Command.Root>
      </FacetedFilter>
    );
  },
});

export function isListFilter(filter: unknown): filter is ListFilter {
  if (typeof filter !== "object") return false;
  if (!filter) return false;

  return "_type" in filter && filter._type === "list";
}

export const listFilter: FilterFn<any> = (row, id, comparator: ListFilter) => {
  if (!comparator) return true;

  const rowValue = row.getValue(id);

  // If custom filter function is provided, use it with selected values as array
  if (comparator.filterFunction) {
    const selectedValues = Array.isArray(comparator.value) ? comparator.value : [comparator.value];
    return comparator.filterFunction(rowValue, selectedValues);
  }

  // Default behavior
  if (comparator.mode === "single") {
    return rowValue === comparator.value;
  }

  if (comparator.mode === "multiple") {
    return (comparator.value as ListValue[]).includes(rowValue as ListValue);
  }

  return true;
};
